import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import JsonLd from '../components/JsonLd';

// Messages are delivered by FormSubmit (formsubmit.co), a free form-to-email
// service that works on static hosting. The first message triggers a one-time
// activation email to this address; after activating, messages arrive normally.
const CONTACT_EMAIL = 'alphaapps124@gmail.com';

const ContatoPage = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    mensagem: ''
  });
  const [honey, setHoney] = useState(''); // hidden anti-spam field
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honey) return; // bots fill the hidden field; silently ignore
    setSending(true);
    setSendError(false);
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${CONTACT_EMAIL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          nome: formData.nome,
          email: formData.email,
          mensagem: formData.mensagem,
          _replyto: formData.email,
          _subject: 'Nova mensagem — Calculadora de Horas',
          _template: 'table',
          _captcha: 'false'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || String(data.success) === 'false') throw new Error(data.message || 'send failed');
      setSubmitted(true);
      setFormData({ nome: '', email: '', mensagem: '' });
    } catch {
      setSendError(true);
    } finally {
      setSending(false);
    }
  };

  const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Contato — Calculadora de Horas')}&body=${encodeURIComponent(formData.mensagem)}`;

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Contato — Calculadora de Horas</title>
        <meta name="description" content="Entre em contato com a equipe do Calculadora de Horas. Envie dúvidas, sugestões ou reporte problemas com nossas calculadoras trabalhistas CLT." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/contato" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Contato — Calculadora de Horas" />
        <meta property="og:description" content="Entre em contato com a equipe do Calculadora de Horas. Envie dúvidas, sugestões ou reporte problemas com nossas calculadoras trabalhistas CLT." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/contato" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contato — Calculadora de Horas" />
        <meta name="twitter:description" content="Entre em contato com a equipe do Calculadora de Horas. Envie dúvidas, sugestões ou reporte problemas com nossas calculadoras trabalhistas CLT." />

        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/contato" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/contato" />
      </Helmet>
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Contato","item":"https://calculadoradehoras.net.br/contato"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4" aria-label="Breadcrumb">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Contato</span>
        </nav>
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Contato
          </h1>
          <p className="text-lg text-body-text">
            Tem alguma dúvida, sugestão ou encontrou um erro? Entre em contato conosco.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto">
          {submitted ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">✓</div>
              <h2 className="text-2xl font-heading font-bold text-success-green mb-2">
                Mensagem Enviada!
              </h2>
              <p className="text-body-text">
                Obrigado pelo contato. Responderemos em breve.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="text"
                name="_honey"
                value={honey}
                onChange={(e) => setHoney(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ display: 'none' }}
              />
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-dark-text mb-2">
                  Nome
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  className="w-full h-12 px-4 border-2 border-border-gray rounded-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                  data-testid="nome-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-dark-text mb-2">
                  E-mail
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full h-12 px-4 border-2 border-border-gray rounded-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label htmlFor="mensagem" className="block text-sm font-medium text-dark-text mb-2">
                  Mensagem
                </label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 border-2 border-border-gray rounded-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary resize-none"
                  data-testid="mensagem-input"
                />
              </div>

              {sendError && (
                <p className="text-warning-red text-sm" role="alert" data-testid="send-error">
                  Não foi possível enviar agora. Tente novamente ou{' '}
                  <a href={mailtoHref} className="underline font-semibold">envie direto pelo seu e-mail</a>.
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                className="w-full h-12 bg-blue-primary hover:bg-blue-600 text-white font-bold rounded-lg transition-colors disabled:opacity-60"
                data-testid="submit-button"
              >
                {sending ? 'Enviando…' : 'Enviar Mensagem'}
              </button>

              <p className="text-sm text-body-text text-center">
                Ou escreva diretamente para{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-primary hover:underline">{CONTACT_EMAIL}</a>
              </p>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default ContatoPage;
